import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import axios from "axios";
import { Upload, FileText, Trash2, Download, Eye, EyeOff, ArrowLeft } from "lucide-react";

const CATEGORIES = {
  pedido_medico: "Pedido médico",
  resultado_exame: "Resultado de exame",
  receita: "Receita",
  documento_pessoal: "Documento pessoal",
  comprovante: "Comprovante",
  outros: "Outros",
};

const CAT_COLOR = {
  pedido_medico: "bg-blue-50 text-blue-700",
  resultado_exame: "bg-emerald-50 text-emerald-700",
  receita: "bg-amber-50 text-amber-700",
  documento_pessoal: "bg-slate-100 text-slate-700",
  comprovante: "bg-purple-50 text-purple-700",
  outros: "bg-slate-100 text-slate-600",
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function FPDocuments() {
  const { id: bid } = useParams();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [category, setCategory] = useState("outros");
  const fileRef = useRef();

  const load = () => api.get(`/fp/documents/${bid}`).then((r) => setDocs(r.data)).finally(() => setLoading(false));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [bid]);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) return toast.error("Arquivo maior que 15MB");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const url = `${BACKEND_URL}/api/fp/documents/${bid}/upload?category=${category}&title=${encodeURIComponent(file.name)}`;
      await axios.post(url, fd, { withCredentials: true });
      toast.success("Documento enviado");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro no upload");
    } finally { setUploading(false); e.target.value = ""; }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover este documento?")) return;
    try {
      await api.delete(`/fp/documents/${id}`);
      toast.success("Documento removido");
      setDocs((d) => d.filter((x) => x.id !== id));
    } catch { toast.error("Erro"); }
  };

  const toggleVisibility = async (doc) => {
    const newVis = doc.visibility === "private" ? "shared_with_pro" : "private";
    try {
      await api.patch(`/fp/documents/${doc.id}/visibility`, {
        visibility: newVis, visible_to_ids: doc.visible_to_ids || [],
      });
      toast.success(newVis === "private" ? "Documento privado" : "Compartilhado com profissionais vinculados");
      load();
    } catch { toast.error("Erro"); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="fp-documents">
      <Link to={`/filho-presente/familiares/${bid}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#0B2B62] mb-4">
        <ArrowLeft className="w-4 h-4" /> Voltar ao perfil
      </Link>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#0B2B62] mb-2">Documentos</h1>
      <p className="text-slate-600 text-sm mb-6">Pedidos médicos, exames, receitas e comprovantes vinculados ao beneficiário.</p>

      {/* Upload */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
          <label className="text-sm font-medium text-slate-700">Categoria:</label>
          <select value={category} onChange={(e) => setCategory(e.target.value)}
            data-testid="fp-doc-category"
            className="px-3 py-2 border border-slate-300 rounded-md text-sm">
            {Object.entries(CATEGORIES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
          <input ref={fileRef} type="file" onChange={onFile}
            accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
            data-testid="fp-doc-file"
            className="hidden" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="fp-doc-upload-btn"
            className="ml-auto inline-flex items-center gap-2 bg-[#0B2B62] text-white px-4 py-2 rounded-lg hover:bg-slate-800 disabled:opacity-50 text-sm">
            <Upload className="w-4 h-4" /> {uploading ? "Enviando..." : "Enviar documento"}
          </button>
        </div>
        <div className="text-xs text-slate-500 mt-2">Aceita PDF, JPG, PNG, HEIC — até 15MB.</div>
      </div>

      {/* List */}
      {loading ? <div className="text-slate-500">Carregando…</div>
        : docs.length === 0 ? (
          <div className="bg-white border-2 border-dashed border-slate-300 rounded-xl p-10 text-center">
            <FileText className="w-10 h-10 text-slate-400 mx-auto mb-3" />
            <div className="text-slate-700">Nenhum documento anexado ainda.</div>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl divide-y">
            {docs.map((d) => (
              <div key={d.id} className="p-4 flex items-center gap-3 flex-wrap" data-testid={`fp-doc-${d.id}`}>
                <FileText className="w-5 h-5 text-slate-400 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-slate-800 truncate">{d.title || d.original_filename}</div>
                  <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className={`px-2 py-0.5 rounded ${CAT_COLOR[d.category] || CAT_COLOR.outros}`}>
                      {CATEGORIES[d.category] || d.category}
                    </span>
                    <span>{new Date(d.created_at).toLocaleDateString("pt-BR")}</span>
                    <span>{(d.size / 1024).toFixed(0)}KB</span>
                    <span className={`inline-flex items-center gap-1 ${d.visibility === "private" ? "text-slate-500" : "text-emerald-700"}`}>
                      {d.visibility === "private" ? <><EyeOff className="w-3 h-3" /> Privado</> : <><Eye className="w-3 h-3" /> Compartilhado</>}
                    </span>
                  </div>
                </div>
                <button onClick={() => toggleVisibility(d)} title="Alternar visibilidade"
                  data-testid={`fp-doc-toggle-${d.id}`}
                  className="text-slate-500 hover:text-[#0B2B62] p-1"><Eye className="w-4 h-4" /></button>
                <a href={`${BACKEND_URL}/api/fp/documents/download/${d.id}`} target="_blank" rel="noreferrer"
                  className="text-slate-500 hover:text-[#0B2B62] p-1" title="Baixar">
                  <Download className="w-4 h-4" />
                </a>
                <button onClick={() => remove(d.id)} data-testid={`fp-doc-remove-${d.id}`}
                  className="text-slate-400 hover:text-red-600 p-1" title="Remover">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}
